import CoverableDetailsTemplate from '../../../../templates/CoverableDetailsTemplate';
import WizardPagetemplate from '../../../../templates/WizardPagetemplate';
import { useHippoText } from '../../../../hooks/useHippoText';
import { allCoverablesOnsaAtom } from '../../../../recoil/atoms/allCoverablesOnsaAtom';
import { useRecoilValue } from 'recoil';
import { getOnsaCurrentProductSelector } from '../../../../recoil/selectors/getOnsaCurrentProductSelector';
import { getCoverableIndex } from '../../../../utils/coverablesUtil';
import { useUpdateCoverableInformation } from '../../../../recoil/hooks/useUpdateCoverableInformation';
import { DateField } from '@flos/react-ui';
import { useSaveCoverableInformation } from '../../../../hooks/useSaveCoverableInformation';
import type { CoverableInformationToUpdateDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa';
import { useState } from 'react';
import { convertDateToLocalDateDTO, convertLocalDateDTOToDate } from '../../../../utils/dateUtil';
import { isRequestPendingAtom } from '../../../../recoil/atoms/isRequestPendingAtom';
import { track } from '../../../../utils/trackingUtil';

const LeasingPeriodStep = ({
    goNext,
    goPrevious
}: {
    goNext: () => void;
    goPrevious: () => void;
}): React.JSX.Element => {
    const isRequestPending = useRecoilValue(isRequestPendingAtom);
    const coverables = useRecoilValue(allCoverablesOnsaAtom);
    const currentProduct = useRecoilValue(getOnsaCurrentProductSelector);
    const getHippoText = useHippoText();
    const { cars = [] } = coverables;
    const coverableIndex = getCoverableIndex(cars, currentProduct.fixedId);
    const coverable = cars[coverableIndex];
    const { updateCoverableInformation } = useUpdateCoverableInformation();
    const { saveCoverableInformation } = useSaveCoverableInformation();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const onNext = (): void => {
        setIsLoading(true);
        const updatedCoverable: CoverableInformationToUpdateDTO = {
            car: {
                publicId: coverable.publicId,
                fixedId: coverable.fixedId,
                leasingStartDate: coverable.leasingStartDate,
                leasingEndDate: coverable.leasingEndDate
            },
            productToUpdate: currentProduct.flavourName
        };

        void saveCoverableInformation(updatedCoverable).then(() => {
            track(`${currentProduct.flavourName}ContentCoverableInformationLeasingPeriod`, [
                { leasingStartDate: coverable.leasingStartDate },
                { leasingEndDate: coverable.leasingEndDate }
            ]);
            setIsLoading(false);
            goNext();
        });
    };

    return (
        <WizardPagetemplate
            onNext={onNext}
            onPrevious={goPrevious}
            disableNext={
                isLoading ||
                !coverable.leasingStartDate ||
                !coverable.leasingEndDate ||
                isRequestPending
            }
            disablePrevious={isRequestPending || isLoading}
            isNextLoading={isLoading || isRequestPending}
        >
            <CoverableDetailsTemplate
                header={getHippoText('DSS-COVERABLE-INFORMATION-STEP-LEASING-PERIOD-HEADER')}
                subheader={getHippoText('DSS-COVERABLE-INFORMATION-STEP-LEASING-PERIOD-TEXT')}
            >
                <DateField
                    label={getHippoText('DSS-COVERABLE-INFORMATION-STEP-LEASING-START-DATE')}
                    onChange={(date: Date): void => {
                        updateCoverableInformation({
                            leasingStartDate: convertDateToLocalDateDTO(date)
                        });
                    }}
                    maxDate={new Date().setDate(new Date().getDate() - 1)}
                    selected={
                        coverable.leasingStartDate !== undefined
                            ? convertLocalDateDTOToDate(coverable.leasingStartDate)
                            : undefined
                    }
                    disabled={isLoading || isRequestPending}
                />
                <DateField
                    label={getHippoText('DSS-COVERABLE-INFORMATION-STEP-LEASING-END-DATE')}
                    onChange={(date: Date): void => {
                        updateCoverableInformation({
                            leasingEndDate: convertDateToLocalDateDTO(date)
                        });
                    }}
                    minDate={new Date()}
                    selected={
                        coverable.leasingEndDate !== undefined
                            ? convertLocalDateDTOToDate(coverable.leasingEndDate)
                            : undefined
                    }
                    disabled={isLoading || isRequestPending}
                />
            </CoverableDetailsTemplate>
        </WizardPagetemplate>
    );
};

export default LeasingPeriodStep;
